import React, {useContext, useEffect, useRef, useState} from 'react';
import GisCardDetails from "./gis-card-details.jsx";
import GisCardList from "./gis-card-list.jsx";
import {GisContext} from "../context/gis-context.jsx";
import GisUtils from "../utils/gis-utils.jsx";
import {FaAngleDoubleLeft, FaFilter, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import Button from "../../../../components/button/button.jsx";
import InputSwitch from "../../../../components/input/input-switch.jsx";

const GisCard = ({handleDetails, markers, geoItems, geoLayers, events, map, overlay, eventsCount, selectCamerasEvent}) => {
    const {menu, changeMenu, target, geoLayerChecked, setGeoLayerChecked, coreLib} = useContext(GisContext);

    const targetRef = useRef("");
    const markerRef = useRef(undefined);
    const playerRef = useRef(undefined);

    const [showMarker, setShowMarker] = useState(false);
    const [highLight, setHighLight] = useState(false);
    const [filterTarget, setFilterTarget] = useState("");

    const [showLabel, setShowLabel] = useState(false);

    const [openModal, setOpenModal] = useState(false);

    useEffect(() => {
        if (markerRef.current !== target.eventSelected?.id) {
            if (markerRef.current) {
                const marker = markers.get(markerRef.current) ?? geoItems.get(markerRef.current);
                if (marker) {
                    let visible = menu[targetRef.current]?.show ?? false;
                    if (marker.type === "point") visible = geoLayerChecked.includes(marker.id_);

                    marker.setAnimation(null);
                    marker.setVisible(visible);
                    marker.setZIndex(GisUtils.getPriority(marker.type, marker.alarm));
                }
            }

            markerRef.current = target.eventSelected?.id;
        }

        if (targetRef.current !== target.selected) setFilterTarget("");

        targetRef.current = target.selected;
    }, [target]);

    useEffect(() => {
        if (target.selected) {
            setShowMarker(!!menu[target.selected]?.show);
            setHighLight(!!menu[target.selected]?.highlight);
        }
    }, [menu, target]);

    const handleVisibility = (id, checked) => {
        const newMenu = {...menu};

        let newChecked = [...geoLayerChecked];

        let ArrayMap = [...markers];
        if (target.selected === "point") ArrayMap = [...geoItems]
        const filter = ArrayMap.filter(([, marker]) => marker.type === target.selected);

        switch (id) {
            case "show-marker":
                if (target.selected === "point") {
                    setOpenModal(true);
                    return;
                }

                filter.forEach(([, marker]) => {
                    let visible = checked;
                    if (markerRef.current === marker.id) visible = true;

                    marker.setVisible(visible);
                });

                const find = Object.keys(newMenu).find(key => key !== target.selected && !!newMenu[key]?.highlight);
                if (find && checked) newMenu[find].highlight = false;

                newMenu[target.selected].show = checked;
                setShowMarker(checked);

                break;
            case "show-layer":
                const layersId = [];
                [...geoLayers].forEach(([, layers]) => layers.forEach(layer => {
                    if (!layersId.includes(layer.id_)) layersId.push(layer.id_);
                    layer.setVisible(checked);
                }));

                newChecked = checked ? [...newChecked, ...layersId] : newChecked.filter(item => !layersId.includes(item));

                newMenu[target.selected].show = checked;
                setGeoLayerChecked(newChecked);
                setShowMarker(checked);

                break;
            case "highlight-marker":
                if (target.selected === "point" && !geoLayerChecked.some(item => item.includes("POINT"))) {
                    setOpenModal(true);
                    return
                }

                if (target.selected !== "point" && checked) newChecked = newChecked.filter(item => !item.includes("POINT"));
                else if (!checked) newChecked = menu.point.prevChecked;

                const show = checked ? true : newMenu[target.selected].prevShow;
                [...markers].forEach(([, marker]) => {
                    let visible = checked ? false : menu[marker.type].prevShow;
                    marker.setVisible(marker.type === target.selected ? show : visible);
                });

                [...geoItems].forEach(([, marker]) => marker.setVisible(newChecked.some(item => item === marker.id_)));

                Object.keys(newMenu).filter(key => newMenu[key].highlight !== undefined).forEach(key => {
                    const show = checked ? false : newMenu[key].prevShow;

                    newMenu[key] = {
                        ...newMenu[key],
                        prevShow: newMenu[key].show,
                        show: show,
                        highlight: false
                    };

                    if (key === "point") newMenu.point.prevChecked = geoLayerChecked;
                });

                setGeoLayerChecked(newChecked);

                newMenu[target.selected].show = show
                newMenu[target.selected].highlight = checked;
                setHighLight(checked);

                if (checked) setShowMarker(checked);

                break;
        }

        changeMenu(newMenu);
    }

    const centralizeEvents = async () => {
        const bounds = new coreLib.LatLngBounds();

        if (target.selected === "zone") {
            if (!geoLayerChecked.some(item => item.includes("ZONE"))) {
                await handleVisibility("show-layer", true);
            }

            [...geoLayers].flatMap(([, layers]) => layers.filter(layer => layer.visible))
                .flatMap(layer => layer.getPath().forEach(latLng => bounds.extend(latLng)));

        } else if (target.selected === "point") {
            const geoItemsFilter = [...geoItems].filter(([, marker]) => marker.visible);

            if (!geoItemsFilter.length) {
                setOpenModal(true);
                return;
            }

            geoItemsFilter.forEach(([, marker]) => bounds.extend(new coreLib.LatLng(marker.getPosition())));

        } else {
            let markersFilter = [...markers].filter(([, marker]) => marker.type === target.selected);
            if (!markersFilter.length) return;

            markersFilter.forEach(([, marker]) => bounds.extend(new coreLib.LatLng(marker.getPosition())));

            handleVisibility("show-marker", true);
        }

        map.fitBounds(bounds);
    }

    const closeDetails = () => handleDetails(null, null);

    const toggleFilter = (value) => {
        if (value === "search" && filterTarget !== "search" && target.selected === "point" && !geoLayerChecked.some(item => item.includes("POINT"))) {
            setOpenModal(true);
        }

        setFilterTarget(value !== filterTarget ? value : "");
    }

    const handleLabel = () => {
        [...markers].filter(([, marker]) => marker.type === "garrison").forEach(([, marker]) => {
            const {contingentName} = marker.details

            if (!showLabel) marker.setLabel({
                text: contingentName.split(" | ")[0]
            })
            else marker.setLabel(undefined);
        })

        setShowLabel(!showLabel);
    }

    if (!target.selected) return;

    return (
        <div id="menu-details" className="card">
            <Card  id="menu-details">
                <Card.Body id="menu-body" className={"card-body h-full " + target.selected}>
                    <span className="hide-details" title="Fechar" onClick={closeDetails}>
                        <FaAngleDoubleLeft />
                    </span>

                    <div id="config-div">
                        <h3 className="font-bold flex gap-2 items-center">
                            <InputSwitch
                                checked={showMarker}
                                onChange={(checked) => handleVisibility(target.selected === "zone" ? "show-layer" : "show-marker", checked)}
                            />

                            {GisUtils.getGroupName(target.selected)}
                            {
                                eventsCount[target.selected] > 0 &&
                                <div className='bg-[#ed5565] text-white h-[15px] w-[15px] text-center place-content-center text-[11px] rounded-[100%]'>
                                    {eventsCount[target.selected]}
                                </div>
                            }
                        </h3>

                        <div className="flex items-center gap-2 mt-2">
                            {
                                menu[target.selected]?.highlight !== undefined &&
                                <>
                                    <Button
                                        className={"centralize-events" + (highLight ? " selected" : "")}
                                        style={{"fontSize": "13px"}}
                                        onClick={() => handleVisibility("highlight-marker", !highLight)}
                                    >
                                        Destacar
                                    </Button>
                                </>
                            }

                            <Button className="centralize-events" onClick={centralizeEvents}>
                                Centralizar
                            </Button>

                            <Button className={"centralize-events !w-[35px]" + (filterTarget === "search" ? " selected" : "")}
                                      checked={filterTarget === "search"}
                                      title={target.selected === "point" ? "Pesquisar ponto" : target.selected === "sos" ? "Pesquisar cidadão" : "Pesquisar"}
                                    onClick={() => toggleFilter("search")}
                            >
                                <FaSearch />
                            </Button>

                            {
                                ["attendance", "garrison", "capturePlace", "contactid",  "sos", "vms"].includes(target.selected) &&
                                <Button className={"centralize-events !w-[35px]" + (filterTarget === "select" ? " selected" : "")} checked={filterTarget === "select"}
                                          title={"Filtros"} onClick={() => toggleFilter("select")}
                                >
                                    <FaFilter />
                                </Button>
                            }
                        </div>

                        {
                            target.selected === "garrison" &&
                            <InputCheckbox
                                id='garrison-prefix'
                                className="mt-2"
                                label='Exibir prefixo da guarnição'
                                onChange={handleLabel}
                                checked={showLabel}
                            />
                        }
                    </div>

                    <GisCardList
                        map={map}
                        events={events}
                        markers={markers}
                        geoItems={geoItems}
                        geoLayers={geoLayers}
                        filterTarget={filterTarget}
                        handleDetails={handleDetails}
                        targetRef={targetRef}
                        markerRef={markerRef}
                        open={openModal}
                        setOpen={setOpenModal}
                        selectCamerasEvent={selectCamerasEvent}
                    />

                    {
                        target.selected !== "zone" && target.eventSelected &&
                        <GisCardDetails
                            event={target.eventSelected}
                            markers={markers}
                            map={map}
                            overlay={overlay}
                            player={playerRef}
                        />
                    }
                </Card.Body>
            </Card>
        </div>
    );
};

export default GisCard;